// 服务器配置 - 统一管理所有服务器地址
//
// 主备域名（web 侧 widget/jssdk 专用；原生 SDK 的 /sdk/sessions、服务端下发
// ws_url 那套走 settings 配置层，不在这里）：主域名 api.lanechat-cdn.com 域名
// 韧性更好，但部分网络可能被墙/污染；fallback 是老域名 api.lane.chat。
//
// 切换判据是连接层失败——fetch reject（网络错误）、超时、WS onerror/握手失败；
// 业务 4xx/5xx 代表"已连通"，不触发切换（判定逻辑在 api.js/websocket.js 里，
// 这里只负责"当前生效是哪个域名"这一件事的唯一真源）。
//
// REST 与 WS 共用这同一份"当前生效域名"判定，不会出现 REST 走 A、WS 走 B。
// 判定结果用 sessionStorage 粘住本页面会话，避免每个请求都先吃一次主域名超时；
// 下一个页面生命周期（新 tab / 刷新）重新从主域名开始尝试，不做定时回切。
//
// 现有覆盖机制优先级不变：window.laneChatConfig 显式配置 apiUrl/wsUrl（本地调试
// /测试床用）永远直接生效，完全不经过主备判定——见 resolveServerConfig() 的第一分支。
const API_HOST_PRIMARY = 'api.lanechat-cdn.com';
const API_HOST_FALLBACK = 'api.lane.chat';
const FALLBACK_FLAG_KEY = 'lane_chat_api_fallback';

// 主域名判死的超时：故意比普通业务请求超时（见 api.js 的 30s）短得多——
// 被墙用户等的是"多快切到能用的域名"，不是"要不要再等一等"。3-5s 量级。
export const PRIMARY_PROBE_TIMEOUT_MS = 4000;

// 默认配置
export const ServerConfig = {
    // 默认配置
    default: {
        apiUrl: 'http://localhost:8787/api',
        wsUrl: 'ws://localhost:8787/ws',
        cdnUrl: 'http://localhost:8787/dist'
    },

    // 开发环境配置
    development: {
        apiUrl: 'http://localhost:8787/api',
        wsUrl: 'ws://localhost:8787/ws',  // WebSocket通过/ws路径
        cdnUrl: 'http://localhost:8787/dist'
    },

    // 生产环境配置（主域名版本；主备判定见 resolveServerConfig）
    production: {
        apiUrl: 'https://' + API_HOST_PRIMARY + '/v1',
        wsUrl: 'wss:' + '//' + API_HOST_PRIMARY + '/ws',
        cdnUrl: 'https://' + 'webmanager.lane.chat'
    }
};

// ---------- 纯函数：切换/环境判定的唯一出口，全部不摸 window/sessionStorage ----------
// 下面几个函数是真正的决策逻辑，参数把所有外部状态都显式传进来，方便 node --test
// 直接打表验证，不用在 Node 里 mock window/sessionStorage。

export function isLocalhostHost(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
}

// 当前应该用主域名还是 fallback（不含"要不要走主备逻辑"这层，纯粹是两者之间选一个）。
export function resolveActiveApiHost({ fallbackActive }) {
    return fallbackActive ? API_HOST_FALLBACK : API_HOST_PRIMARY;
}

// 完整配置对象派生：显式覆盖 > 本地开发 > 生产(主/备)。
export function resolveServerConfig({ explicitOverride, hostname, fallbackActive }) {
    if (explicitOverride) {
        return {
            ...ServerConfig.default,
            ...explicitOverride
        };
    }

    if (isLocalhostHost(hostname)) {
        return ServerConfig.development;
    }

    // 生产：apiUrl/wsUrl 按当前生效域名（主/备）动态派生，cdnUrl（静态资源 CDN）
    // 不在本次主备范围内，保持不变。
    const host = resolveActiveApiHost({ fallbackActive });
    return {
        apiUrl: `https://${host}/v1`,
        wsUrl: `wss://${host}/ws`,
        cdnUrl: ServerConfig.production.cdnUrl
    };
}

// 这次配置是否打的是主域名（生产、无显式覆盖、且尚未切到 fallback）。调用方用它
// 决定：要不要用短探测超时、失败后要不要触发一次 fallback 切换重试。显式覆盖
// / 本地开发场景下恒为 false —— 主备逻辑因此天然不介入，覆盖优先级不变。
export function resolveIsPrimaryHost({ explicitOverride, hostname, fallbackActive }) {
    if (explicitOverride) return false;
    if (isLocalhostHost(hostname)) return false;
    return !fallbackActive;
}

// 把一个已经拼好的主域名 URL（REST 的 https:// 或 WS 的 wss://，协议不关心）
// 就地替换成 fallback 域名，用于连接层失败后的"本次请求原地重试"（不用等调用方
// 重新拼 URL）。只在 resolveIsPrimaryHost() 曾为 true 时调用，此时 url 必然
// 包含主域名字面量。
export function rewriteUrlToFallbackHost(url) {
    return url.replace(API_HOST_PRIMARY, API_HOST_FALLBACK);
}

// ---------- 薄封装：读 window/sessionStorage，交给上面的纯函数决策 ----------

function readFallbackFlag() {
    try {
        return sessionStorage.getItem(FALLBACK_FLAG_KEY) === '1';
    } catch (e) {
        return false; // 无痕模式等 sessionStorage 不可用时，按主域名处理，不阻塞功能
    }
}

function writeFallbackFlag() {
    try {
        sessionStorage.setItem(FALLBACK_FLAG_KEY, '1');
    } catch (e) { /* 存不了就每次都重判一次主域名，不阻塞功能 */ }
}

// 获取当前环境配置
export function getCurrentConfig() {
    return resolveServerConfig({
        explicitOverride: (typeof window !== 'undefined' && window.laneChatConfig) || null,
        hostname: typeof window !== 'undefined' ? window.location.hostname : '',
        fallbackActive: readFallbackFlag()
    });
}

// 导出便捷方法
export const getApiUrl = () => getCurrentConfig().apiUrl;
export const getWsUrl = () => getCurrentConfig().wsUrl;
export const getCdnUrl = () => getCurrentConfig().cdnUrl;

export function isCurrentApiHostPrimary() {
    return resolveIsPrimaryHost({
        explicitOverride: (typeof window !== 'undefined' && window.laneChatConfig) || null,
        hostname: typeof window !== 'undefined' ? window.location.hostname : '',
        fallbackActive: readFallbackFlag()
    });
}

// 上报一次连接层失败（fetch reject/超时、WS onerror/握手失败），标记切到 fallback
// 域名，本页面会话内粘住。已经是 fallback 时是 no-op。调用方自己先用
// isCurrentApiHostPrimary() 判断要不要调这个——这里不重复判断，保持单一职责。
export function reportApiConnectionFailure() {
    writeFallbackFlag();
}
